// Setup › People on the SettingsPage pattern (BL-189): everyone who works here, a row each (people.list):
// their name; the email they sign in with and what they do as its line; the roles they sign in with, or
// that they don't, as its word. Invite someone is the section's action. A person's Change opens their
// sheet (a FormSheet with nothing to save as a whole: each change saves on its own): their roles, each
// limited to some departments or taken away, a role added; their access (working hours, profile, roles,
// sign-in, sign out everywhere), each in its own dialog; what was given before roles; and what their roles
// add up to (BL-137). The invitations made without Settings follow. Riverstone keeps Settings.tsx's People.
import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Area, Button, ConfirmDialog, Link, RowList, SelectField, type Row } from '@flowology/ui-web/fl';
import { FormSheet, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import { hoursRights } from './hours-model.ts';
import { StaffHoursSheet } from './Hours.tsx';
import { BP_LEDE } from './ledes.ts';
import { LicencesExpiring } from './Licences.tsx';
import { settingsPage } from './parts.ts';
import {
  InviteDialog,
  LimitDialog,
  RolesDialog,
  SignInSwitchDialog,
  type Person,
  type SwitchMode,
  type Unit,
} from './PeopleDialogs.tsx';
import {
  GIVEN_BEFORE,
  invitedWords,
  offersSignOut,
  rolesToAdd,
  settingsRights,
  signInWords,
  whatTheyCanDo,
  type MatrixPermission,
} from './settings-model.ts';
import { untilText } from './grants-model.ts';
import { EditProfile } from './Setup.tsx';
import { setupRights } from './setup-model.ts';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

interface RoleDef {
  key: string;
  name: string;
  permissions: string[];
}
/** An open invitation with no staff record (QA-355): made straight through, not by Settings. */
interface LooseInvitation {
  membershipId: string;
  email: string;
  name: string;
  roleKeys: string[];
  invitedAt: string | null;
  expiresAt: string | null;
  ownerConfirmPending: boolean;
}

export function PeoplePage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const held = nav.permissions ?? [];
  const owner = settingsRights(held).owner;
  const setup = setupRights(held);
  const hours = hoursRights(held);
  const canInvite = setup.staff;
  const canEditProfile = setup.staff;
  const viewerStaffId = nav.staffId ?? null;
  const crumb = usePageCrumb(nav);
  // taking back a permission, and an invitation that hands out access, ask a fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  const [people, setPeople] = useState<Person[] | null>(null);
  const [loose, setLoose] = useState<LooseInvitation[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  // the roles and every permission (roles.list): a person's roles add up to what they may do (BL-137)
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [perms, setPerms] = useState<MatrixPermission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [hoursFor, setHoursFor] = useState<Person | null>(null);
  const [limit, setLimit] = useState<{ person: Person; roleKey: string } | null>(null);
  // the roles dialog, with the change asked for when the server wants a step-up or an "I understand"
  const [rolesFor, setRolesFor] = useState<{
    person: Person;
    start?: string[];
    ask?: 'step_up' | 'field';
  } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [switching, setSwitching] = useState<{
    person: { displayName: string; staffId?: string; membershipId?: string };
    mode: SwitchMode;
  } | null>(null);
  const [signingOut, setSigningOut] = useState<Person | null>(null);
  const roleNames = Object.fromEntries(roles.map((x) => [x.key, x.name]));
  const load = useCallback(async () => {
    try {
      const r = await client.call<{ people: Person[]; invitations?: LooseInvitation[] }>('people.list', {});
      setPeople(r.people);
      setLoose(r.invitations ?? []);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => {
    void load();
    client
      .call<{ businessUnits: Unit[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{ roles: RoleDef[]; permissions: MatrixPermission[] }>('roles.list', {})
      .then((r) => {
        setRoles(r.roles);
        setPerms(r.permissions);
      })
      .catch(() => undefined);
  }, [load]);
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? 'a department';
  const ownerOnly = perms.filter((p) => p.ownerOnly);
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' });
  const done = async (text: string) => {
    await load();
    setSaid(text);
  };
  /** Give or take one role (people.setRoles): straight away, or through the dialog when it asks first. */
  const changeRoles = async (person: Person, keys: string[], text: string) => {
    try {
      await client.call('people.setRoles', { staffId: person.staffId, roleKeys: keys });
      await done(text);
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'step_up_required') setRolesFor({ person, start: keys, ask: 'step_up' });
      else if (reason === 'field_role_confirm') setRolesFor({ person, start: keys, ask: 'field' });
      else setSaid(message(e));
    }
  };
  /** A role's reach: only some departments (roles.setScope), or the whole company. */
  const reach = (p: Person, k: string) => {
    const s = p.scopes.find((x) => x.roleKey === k);
    return s ? `only ${s.businessUnitIds.map(unitName).join(', ') || 'some locations'}` : 'company-wide';
  };
  const rowOf = (p: Person): Row => {
    const signIn = signInWords(p.signIn, timeZone);
    const grants = (p.grants ?? []).map((g) => ({
      text: `${GIVEN_BEFORE}: ${ownerOnly.find((o) => o.key === g.permission)?.description ?? g.permission}, ${
        g.expiresAt ? `until ${when.format(new Date(g.expiresAt))}` : 'no end date'
      }`,
    }));
    return {
      id: p.staffId,
      name: p.displayName,
      lines: [
        {
          // the email they sign in with, before the staff record's (QA-353)
          text: `${p.signInEmail ?? p.email ?? 'No email'}${p.isTechnician ? ' · technician' : ''}${
            p.status !== 'active' ? ` · ${p.status}` : ''
          }`,
        },
        ...(signIn ? [{ text: signIn, tone: 'ink' as const, size: 'sm' as const }] : []),
        ...grants,
      ],
      word:
        p.roleKeys === null
          ? { text: 'Doesn’t sign in', tone: 'muted' }
          : { text: p.roleKeys.map((k) => `${roleNames[k] ?? k}, ${reach(p, k)}`).join(' · ') },
      actions: [
        {
          id: 'change',
          text: 'Change',
          label: `Change ${p.displayName}’s settings`,
          onPress: () => {
            setSaid(null);
            setOpened(p.staffId);
          },
        },
      ],
    };
  };
  const person = people?.find((p) => p.staffId === opened) ?? null;
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('people').label ?? 'People',
            lede: BP_LEDE.people,
          }}
          layers={
            <>
              {person && (
                <PersonSheet
                  person={person}
                  owner={owner}
                  canInvite={canInvite}
                  canEditProfile={canEditProfile}
                  viewerStaffId={viewerStaffId}
                  roles={roles}
                  perms={perms}
                  reach={reach}
                  said={said}
                  timeZone={timeZone}
                  onClose={() => setOpened(null)}
                  onLimit={(roleKey) => setLimit({ person, roleKey })}
                  onRoles={(keys, text) => void changeRoles(person, keys, text)}
                  onChooseRoles={() => setRolesFor({ person })}
                  onSwitch={(mode) => setSwitching({ person, mode })}
                  onSignOut={() => setSigningOut(person)}
                  onHours={() => {
                    setOpened(null);
                    setHoursFor(person);
                  }}
                  onProfile={() => {
                    setOpened(null);
                    setProfileFor(person.staffId);
                  }}
                  onResend={() =>
                    void run(() =>
                      client.call<{ emailed: boolean; signInAt: string | null }>('people.resendInvite', {
                        staffId: person.staffId,
                      }),
                    )
                      .then((r) =>
                        done(invitedWords(r, person.displayName, person.signInEmail ?? 'their email')),
                      )
                      .catch((e: unknown) => setSaid(message(e)))
                  }
                  onCancelInvite={() =>
                    void client
                      .call('people.cancelInvite', { staffId: person.staffId })
                      .then(() => done(`${person.displayName}’s invitation is cancelled.`))
                      .catch((e: unknown) => setSaid(message(e)))
                  }
                  onTakeBack={(permission) =>
                    void run(() => client.call('permissions.revoke', { staffId: person.staffId, permission }))
                      .then(() => done(`Taken back from ${person.displayName}.`))
                      .catch((e: unknown) => setSaid(message(e)))
                  }
                />
              )}
              {hoursFor && (
                <StaffHoursSheet
                  person={hoursFor}
                  rights={hours}
                  timeZone={timeZone}
                  onClose={() => setHoursFor(null)}
                />
              )}
              {profileFor && (
                <EditProfile
                  staffId={profileFor}
                  timeZone={timeZone}
                  onSaved={() => {
                    setProfileFor(null);
                    void load();
                  }}
                  onClose={() => setProfileFor(null)}
                />
              )}
              {limit && (
                <LimitDialog
                  person={limit.person}
                  roleKey={limit.roleKey}
                  roleName={roleNames[limit.roleKey] ?? limit.roleKey}
                  units={units}
                  onClose={() => setLimit(null)}
                  onDone={done}
                />
              )}
              {rolesFor && (
                <RolesDialog
                  person={rolesFor.person}
                  {...(rolesFor.start ? { start: rolesFor.start } : {})}
                  {...(rolesFor.ask ? { asked: rolesFor.ask } : {})}
                  roleNames={roleNames}
                  onClose={() => setRolesFor(null)}
                  onDone={done}
                />
              )}
              {switching && (
                <SignInSwitchDialog
                  person={switching.person}
                  mode={switching.mode}
                  onClose={() => setSwitching(null)}
                  onDone={done}
                />
              )}
              {inviting && (
                <InviteDialog
                  owner={owner}
                  roleNames={roleNames}
                  onClose={() => setInviting(false)}
                  onInvited={done}
                />
              )}
              {signingOut && (
                <ConfirmDialog
                  isOpen
                  title={`Sign ${signingOut.displayName} out everywhere?`}
                  confirmLabel="Sign out everywhere"
                  onClose={() => setSigningOut(null)}
                  onConfirm={async () => {
                    const r = await client
                      .call<{ ended: number }>('people.signOutEverywhere', { staffId: signingOut.staffId })
                      .catch((e: unknown) => {
                        throw new Error(message(e));
                      });
                    await done(
                      r.ended
                        ? `${signingOut.displayName} is signed out everywhere here. They sign in again as usual, or with an emailed code.`
                        : `${signingOut.displayName} wasn’t signed in anywhere here.`,
                    );
                  }}
                >
                  Every browser and iPad of theirs in this company ends now.
                </ConfirmDialog>
              )}
              {ask}
            </>
          }
        >
          <SettingsSection
            title="Everyone who works here"
            line="A person may hold several roles and may do what any of them allows. Only the owner gives roles or limits one to some departments."
            {...(canInvite
              ? {
                  action: (
                    <Button variant="primary" onPress={() => setInviting(true)}>
                      Invite someone
                    </Button>
                  ),
                }
              : {})}
            {...(owner || said
              ? {
                  note: (
                    <>
                      {/* Blueprint: both lists are Who may do what's, beside the roles (the setup audit) */}
                      {owner ? (
                        <>
                          The permissions given to people and the limited roles moved to{' '}
                          <Link href="#/settings/roles">Who may do what</Link>.
                        </>
                      ) : null}
                      {owner && said ? ' ' : null}
                      {said ? <span role="status">{said}</span> : null}
                    </>
                  ),
                }
              : {})}
            status={people ? (people.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
            emptyText="Nobody here yet."
            {...(error ? { failedText: error } : {})}
            onRetry={() => void load()}
          >
            {people && <RowList label="People" rows={people.map(rowOf)} />}
          </SettingsSection>
          {/* the licences expired or expiring soon, for the people who keep the profiles (BL-027) */}
          {canEditProfile && people && <LicencesExpiring timeZone={timeZone} people={people} />}
          {loose.length > 0 && (
            <SettingsSection
              title="Invitations with no staff record"
              line="Made without Settings. Every owner was told of each. Confirm one you expect, or cancel it."
            >
              <RowList
                label="Invitations with no staff record"
                rows={loose.map((l) => ({
                  id: l.membershipId,
                  name: l.name,
                  lines: [
                    { text: `${l.email} · ${l.roleKeys.map((k) => roleNames[k] ?? k).join(', ')}` },
                    {
                      text:
                        signInWords(
                          {
                            status: 'invited',
                            invitedAt: l.invitedAt,
                            expiresAt: l.expiresAt,
                            lapsed: false,
                            ownerConfirmPending: l.ownerConfirmPending,
                          },
                          timeZone,
                        ) ?? '',
                      tone: 'ink',
                      size: 'sm',
                    },
                  ],
                  atRest: 2,
                  actions: [
                    ...(owner && l.ownerConfirmPending
                      ? [
                          {
                            id: 'confirm',
                            text: 'Confirm as owner…',
                            onPress: () =>
                              setSwitching({
                                person: { displayName: l.name, membershipId: l.membershipId },
                                mode: 'confirm',
                              }),
                          },
                        ]
                      : []),
                    ...(canInvite
                      ? [
                          {
                            id: 'cancel',
                            text: 'Cancel invitation',
                            label: `Cancel ${l.name}’s invitation`,
                            onPress: () =>
                              void client
                                .call('people.cancelInvite', { membershipId: l.membershipId })
                                .then(() => done(`${l.name}’s invitation is cancelled.`))
                                .catch((e: unknown) => setSaid(message(e))),
                          },
                        ]
                      : []),
                  ],
                }))}
              />
            </SettingsSection>
          )}
        </SettingsPage>
      </main>
    </div>
  );
}

/**
 * One person's sheet: their roles (each limited or taken away; one added), their access, what was given
 * before roles, and what their roles add up to. Nothing here saves as a whole: each change saves on its
 * own, and says so in the note.
 */
function PersonSheet({
  person: p,
  owner,
  canInvite,
  canEditProfile,
  viewerStaffId,
  roles,
  perms,
  reach,
  said,
  timeZone,
  onClose,
  onLimit,
  onRoles,
  onChooseRoles,
  onSwitch,
  onSignOut,
  onHours,
  onProfile,
  onResend,
  onCancelInvite,
  onTakeBack,
}: {
  person: Person;
  owner: boolean;
  canInvite: boolean;
  canEditProfile: boolean;
  viewerStaffId: string | null;
  roles: readonly RoleDef[];
  perms: readonly MatrixPermission[];
  reach: (p: Person, roleKey: string) => string;
  said: string | null;
  timeZone: string;
  onClose: () => void;
  onLimit: (roleKey: string) => void;
  onRoles: (keys: string[], said: string) => void;
  onChooseRoles: () => void;
  onSwitch: (mode: SwitchMode) => void;
  onSignOut: () => void;
  onHours: () => void;
  onProfile: () => void;
  onResend: () => void;
  onCancelInvite: () => void;
  onTakeBack: (permission: string) => void;
}) {
  const name = (k: string) => roles.find((r) => r.key === k)?.name ?? k;
  const keys = p.roleKeys;
  const toAdd = keys ? rolesToAdd(keys, roles) : [];
  const can = keys !== null && perms.length > 0 ? whatTheyCanDo(keys, roles, perms, p.grants ?? []) : [];
  const n = can.reduce((t, g) => t + g.items.length, 0);
  const describe = (key: string) => perms.find((x) => x.key === key)?.description ?? key;
  return (
    <FormSheet
      isOpen
      title={p.displayName}
      line={`${p.signInEmail ?? p.email ?? 'No email'}${p.isTechnician ? ' · technician' : ''}`}
      isDirty={false}
      isReadOnly
      note={said ?? 'Each change here saves on its own.'}
      onClose={onClose}
      onSubmit={onClose}
    >
      {keys === null ? (
        <p>Doesn’t sign in.</p>
      ) : (
        <RowList
          label={`${p.displayName}’s roles`}
          rows={keys.map((k) => ({
            id: k,
            name: name(k),
            lines: [{ text: reach(p, k) }],
            atRest: 2,
            actions:
              owner && k !== 'owner'
                ? [
                    {
                      id: 'limit',
                      text: p.scopes.some((s) => s.roleKey === k) ? 'Change' : 'Limit',
                      label: `Where ${p.displayName}’s ${name(k)} role applies`,
                      onPress: () => onLimit(k),
                    },
                    // a role taken away takes only what none of their other roles gives (BL-137)
                    ...(keys.length > 1
                      ? [
                          {
                            id: 'take',
                            text: 'Take away',
                            label: `Take ${name(k)} from ${p.displayName}`,
                            onPress: () =>
                              onRoles(
                                keys.filter((x) => x !== k),
                                `${name(k)} is taken from ${p.displayName}.`,
                              ),
                          },
                        ]
                      : []),
                  ]
                : owner && keys.length > 1
                  ? [
                      {
                        id: 'take',
                        text: 'Take away',
                        label: `Take ${name(k)} from ${p.displayName}`,
                        onPress: () =>
                          onRoles(
                            keys.filter((x) => x !== k),
                            `${name(k)} is taken from ${p.displayName}.`,
                          ),
                      },
                    ]
                  : [],
          }))}
        />
      )}
      {owner && keys !== null && toAdd.length > 0 && (
        <SelectField
          label={`Add a role for ${p.displayName}`}
          value=""
          options={[
            { value: '', label: 'Add a role…' },
            ...toAdd.map((r) => ({ value: r.key, label: r.name })),
          ]}
          onChange={(k) => {
            if (k) onRoles([...keys, k], `${p.displayName} has ${name(k)} now, beside their other roles.`);
          }}
        />
      )}
      {canInvite && p.signIn?.status === 'invited' && p.signIn.invitedAt && (
        <>
          <Button onPress={onResend}>Send again</Button>
          <Button onPress={onCancelInvite}>Cancel invitation</Button>
        </>
      )}
      {/* their working hours, for everyone who sees People; changed by those who schedule (BL-014) */}
      <Button onPress={onHours}>Working hours</Button>
      {canEditProfile && <Button onPress={onProfile}>Edit profile</Button>}
      {owner && keys !== null && (
        <>
          <Button onPress={onChooseRoles}>Roles…</Button>
          {p.signIn?.status === 'active' && (
            <Button variant="danger" onPress={() => onSwitch('off')}>
              Turn off sign-in…
            </Button>
          )}
          {p.signIn?.status === 'disabled' && (
            <Button onPress={() => onSwitch('on')}>Turn sign-in back on…</Button>
          )}
          {p.signIn?.status === 'invited' && p.signIn.ownerConfirmPending && (
            <Button onPress={() => onSwitch('confirm')}>Confirm as owner…</Button>
          )}
        </>
      )}
      {offersSignOut({ owner, managesPeople: canInvite, staffId: viewerStaffId }, p) && (
        <Button onPress={onSignOut}>Sign out everywhere…</Button>
      )}
      {/* grants from before roles held owner-only permissions: they run to their date */}
      {p.grants !== null && p.grants.length > 0 && (
        <RowList
          title={GIVEN_BEFORE}
          label={GIVEN_BEFORE}
          rows={p.grants.map((g) => ({
            id: g.permission,
            name: describe(g.permission),
            lines: [{ text: untilText(g.expiresAt, timeZone) }],
            actions: [
              {
                id: 'take',
                text: 'Take back',
                label: `Take back ${describe(g.permission)} from ${p.displayName}`,
                onPress: () => onTakeBack(g.permission),
              },
            ],
          }))}
        />
      )}
      {/* what their roles add up to (BL-137) */}
      {n > 0 && (
        <Area title={`What ${p.displayName} can do`} count={n} level={3}>
          {can.map((g) => (
            <RowList
              key={g.title}
              title={g.title}
              label={g.title}
              rows={g.items.map((i) => ({
                id: i.key,
                name: i.description,
                ...(i.ownerOnly ? { word: { text: 'Owner only', tone: 'wait' as const } } : {}),
                after: { text: ` · ${i.from.join(', ')}` },
              }))}
            />
          ))}
        </Area>
      )}
    </FormSheet>
  );
}
